export const courseData = {
  title: "7 Days to Sovereignty",
  subtitle: "Your Unshackled Living Journey Begins",
  description: "A transformative week-long journey to help you take the first steps toward financial, physical, and mental sovereignty.",
  days: [
    {
      day: 1,
      title: "Understanding Your Current Reality",
      theme: "Awareness",
      icon: "Eye",
      podcastEpisode: {
        title: "The Sovereign Child: Raising Truly Autonomous Humans",
        url: "https://www.youtube.com/watch?v=BiJ3ovDM6Vk&t=1s",
        description: "Rewriting the rules of freedom and what it means to raise children who think for themselves."
      },
      content: {
        intro: "Today we begin by honestly assessing where you stand. Sovereignty starts with awareness—you can't change what you don't acknowledge. Most people sleepwalk through life, never questioning the systems they depend on. Today, we wake up.",
        
        deepDive: {
          title: "The Invisible Chains of Modern Life",
          content: "We live in an age of unprecedented convenience, but that convenience comes at a cost most never consider. Every centralized system you rely on is a potential point of failure—and a lever of control others hold over your life.\n\nConsider this: if your bank account was frozen tomorrow, how would you buy groceries? If supply chains disrupted for two weeks, what would you eat? If the power grid failed, could you heat your home?\n\nThese aren't doomsday scenarios—they're questions that reveal the fragility hidden beneath modern comfort. The 2021 Texas power grid failure left millions without heat in freezing temperatures. The 2023 banking crisis showed how quickly 'safe' institutions can fail. The pandemic revealed just how quickly grocery store shelves can empty.\n\nAwareness isn't about fear—it's about clear-eyed assessment that enables meaningful action."
        },
        
        keyPoints: [
          "Map your dependencies: List every system you rely on (banks, grocery stores, utilities, healthcare, internet, supply chains)",
          "Identify single points of failure: Which dependencies, if disrupted, would immediately impact your daily life?",
          "Recognize the control dynamic: Who controls each system you depend on? What are their incentives?",
          "Assess your vulnerability timeline: How long could you sustain yourself if each system failed?",
          "Identify your opportunity: Every dependency is a chance to build resilience and reclaim autonomy"
        ],
        
        tutorial: {
          title: "Creating Your Dependency Audit",
          steps: [
            {
              step: 1,
              title: "List Your Daily Systems",
              description: "Write down every system you interact with in a typical day: banking apps, grocery stores, gas stations, utilities, internet, streaming services, social media, healthcare providers, pharmacies."
            },
            {
              step: 2,
              title: "Rate Each Dependency",
              description: "Score each from 1-5: How critical is it? (1=convenience, 5=survival). How easily disrupted? (1=very stable, 5=fragile). How replaceable? (1=many alternatives, 5=no alternatives)."
            },
            {
              step: 3,
              title: "Identify Your Top 5 Vulnerabilities",
              description: "Which dependencies scored highest across all three metrics? These are your priority areas for building sovereignty."
            },
            {
              step: 4,
              title: "Envision Alternatives",
              description: "For each top vulnerability, brainstorm at least one alternative or backup. Don't judge feasibility yet—just generate possibilities."
            }
          ]
        },
        
        caseStudy: {
          title: "The 2021 Texas Power Crisis",
          content: "In February 2021, a severe winter storm caused the Texas power grid to fail for over four days. 4.5 million homes lost power in freezing temperatures. At least 246 people died. Water treatment plants failed, leaving millions without safe drinking water.\n\nThose who fared best weren't necessarily wealthy—they were prepared. They had alternative heat sources (wood stoves, propane heaters), stored water, food that didn't require refrigeration, and community connections to share resources.\n\nThe lesson: Resilience isn't about predicting specific disasters. It's about reducing single points of failure across all your essential systems."
        },
        
        expertQuote: {
          quote: "The greatest danger in times of turbulence is not the turbulence itself, but to act with yesterday's logic.",
          author: "Peter Drucker",
          context: "Management thinker"
        },
        
        action: "Create a comprehensive 'Dependency Audit' using the tutorial above. Be brutally honest—the goal isn't to feel bad, but to see clearly. Share your top 3 vulnerabilities in your journal.",
        
        reflection: "What surprised you most about your dependency audit? Which dependencies feel most uncomfortable now that you've named them? What would your grandfather think about how dependent modern life has become?"
      },
      
      additionalResources: [
        {
          title: "Antifragile: Things That Gain from Disorder",
          author: "Nassim Nicholas Taleb",
          type: "Book",
          url: "https://www.amazon.com/Antifragile-Things-That-Disorder-Incerto/dp/0812979680",
          description: "Essential reading on building systems that get stronger under stress."
        },
        {
          title: "The Survival Podcast",
          author: "Jack Spirko",
          type: "Podcast",
          url: "https://www.thesurvivalpodcast.com/",
          description: "Daily podcast on modern survivalism, permaculture, and self-reliance."
        },
        {
          title: "When the Grid Goes Dark",
          author: "Ted Koppel",
          type: "Book",
          url: "https://www.amazon.com/Lights-Out-Cyberattack-Nation-Unprepared/dp/0553419986",
          description: "Investigation into grid vulnerability and what happens when systems fail."
        }
      ]
    },
    {
      day: 2,
      title: "The Foundation: Sound Money",
      theme: "Financial Sovereignty",
      icon: "Wallet",
      podcastEpisode: {
        title: "Deflation, Bitcoin, and the Future of Money with Jeff Booth",
        url: "https://www.youtube.com/watch?v=9LMPuyJKv_s",
        description: "Exploring economic transformation and Bitcoin's potential as a deflationary asset."
      },
      content: {
        intro: "You cannot build sovereignty on broken money. Every hour you work, every dollar you save, is being silently diluted by monetary expansion. Today we explore why Bitcoin matters and how it offers an exit from a system designed to transfer wealth from savers to asset holders.",
        
        deepDive: {
          title: "The Hidden Tax: Understanding Monetary Debasement",
          content: "Since 1971, when the US abandoned the gold standard, the Federal Reserve has expanded the money supply by over 8,000%. This isn't abstract economics—it's a direct tax on your time and labor.\n\nWhen you save $10,000 and the money supply doubles, you still have $10,000—but its purchasing power has been cut in half. This is why your grandparents could buy a house on a single income, while today two incomes barely cover rent.\n\nAs Jeff Booth explains in 'The Price of Tomorrow': 'We've been conditioned to believe that mild inflation is normal and healthy. But inflation is simply a transfer of wealth from those who save and work to those who own assets and have access to newly printed money.'\n\nBitcoin offers an alternative: money with a fixed supply of 21 million coins that can never be inflated. It's not about getting rich—it's about opting out of a system that systematically impoverishes savers."
        },
        
        keyPoints: [
          "Fiat currency is designed to lose value: 2% annual inflation means your money loses half its value every 35 years",
          "Cantillon Effect: New money benefits those closest to its creation (banks, governments) before raising prices for everyone else",
          "Bitcoin's fixed supply: Only 21 million will ever exist—no central authority can create more",
          "Self-custody means true ownership: 'Not your keys, not your coins'—Bitcoin on an exchange isn't really yours",
          "Start with understanding, not speculation: The goal is sovereignty, not price appreciation"
        ],
        
        tutorial: {
          title: "Your First Steps to Bitcoin Self-Custody",
          steps: [
            {
              step: 1,
              title: "Educate Before You Invest",
              description: "Read 'The Bitcoin Standard' by Saifedean Ammous or watch the recommended podcast episode. Understand WHY before HOW."
            },
            {
              step: 2,
              title: "Start Small on a Reputable Exchange",
              description: "Buy $50-100 worth of Bitcoin on Swan Bitcoin, River, or Cash App. These are US-regulated and beginner-friendly."
            },
            {
              step: 3,
              title: "Learn About Hardware Wallets",
              description: "Research devices like Coldcard, Trezor, or Ledger. These keep your Bitcoin keys offline and in your control."
            },
            {
              step: 4,
              title: "Practice with Small Amounts",
              description: "Once comfortable, withdraw a small amount to a software wallet (BlueWallet, Muun) to practice self-custody."
            },
            {
              step: 5,
              title: "Graduate to Hardware Wallet",
              description: "When ready, purchase a hardware wallet and transfer your Bitcoin to true self-custody. Your keys, your coins."
            }
          ]
        },
        
        caseStudy: {
          title: "Lebanon's Currency Collapse (2019-Present)",
          content: "In 2019, the Lebanese pound began a collapse that would see it lose over 95% of its value. Citizens watched their life savings evaporate as banks froze withdrawals and the currency hyperinflated.\n\nThose with Bitcoin could preserve their wealth and transact freely when banks locked their doors. As one Lebanese citizen explained: 'Bitcoin became our only way to receive money from family abroad and pay for necessities.'\n\nThis isn't a future scenario—it's happening now in countries around the world. Argentina, Turkey, Nigeria, Venezuela—billions of people live under monetary systems that actively destroy their savings."
        },
        
        expertQuote: {
          quote: "The root problem with conventional currency is all the trust that's required to make it work. The central bank must be trusted not to debase the currency, but the history of fiat currencies is full of breaches of that trust.",
          author: "Satoshi Nakamoto",
          context: "Bitcoin Creator, 2009"
        },
        
        action: "If you don't own any Bitcoin, research how to buy a small amount ($50-100) on Swan Bitcoin or River. If you already own some, research self-custody options and watch the Jeff Booth episode linked above.",
        
        reflection: "How has inflation affected your life in the past year? What would change if you could save in money that can't be debased? What percentage of your savings do you want to protect from monetary debasement?"
      },
      
      additionalResources: [
        {
          title: "The Bitcoin Standard",
          author: "Saifedean Ammous",
          type: "Book",
          url: "https://saifedean.com/thebitcoinstandard/",
          description: "The definitive book on Bitcoin's economic foundations and monetary history."
        },
        {
          title: "The Price of Tomorrow",
          author: "Jeff Booth",
          type: "Book",
          url: "https://www.amazon.com/Price-Tomorrow-Deflation-Abundant-Future/dp/1999257405",
          description: "Why deflation is natural and how technology is changing the economy."
        },
        {
          title: "Bitcoin Fundamentals with Preston Pysh",
          author: "Preston Pysh",
          type: "Podcast",
          url: "https://www.theinvestorspodcast.com/bitcoin-fundamentals/",
          description: "In-depth Bitcoin analysis from a value investing perspective."
        },
        {
          title: "A Beginner's Guide to Bitcoin",
          author: "Lynn Alden",
          type: "Article",
          url: "https://www.lynalden.com/invest-in-bitcoin/",
          description: "Comprehensive guide to understanding Bitcoin from a macro perspective."
        },
        {
          title: "The Bullish Case for Bitcoin",
          author: "Vijay Boyapati",
          type: "Article",
          url: "https://vijayboyapati.medium.com/the-bullish-case-for-bitcoin-6ecc8bdecc1",
          description: "Long-form exploration of why Bitcoin represents a paradigm shift in money."
        },
        {
          title: "Bitcoin Resources",
          author: "Seb Bunney",
          type: "Website",
          url: "https://www.yourbitwiki.com/",
          description: "Curated Bitcoin education resources for all levels."
        }
      ]
    },
    {
      day: 3,
      title: "Food as Freedom",
      theme: "Food Independence",
      icon: "Sprout",
      content: {
        intro: "The average meal travels 1,500 miles to reach your plate, passing through a fragile supply chain controlled by a handful of corporations. Today we explore how to shorten that distance, strengthen your food security, and reconnect with the most fundamental form of sovereignty: knowing where your food comes from.",
        
        deepDive: {
          title: "The Fragility of Industrial Food Systems",
          content: "In 1900, 40% of Americans lived on farms. Today, less than 2% do. We've outsourced the most essential human activity—feeding ourselves—to a system optimized for efficiency over resilience.\n\nThe 2020 pandemic exposed this fragility: empty grocery shelves, meat processing plant shutdowns, and supply chain chaos. Yet farmers were destroying crops and euthanizing livestock because the system couldn't adapt.\n\nFood sovereignty isn't about becoming a full-time farmer. It's about reducing your dependence on a system with too many single points of failure. Every tomato you grow, every local farmer you support, every preserved jar in your pantry is a step toward resilience.\n\nAs Joel Salatin says: 'If you think organic food is expensive, have you priced cancer lately?' The true cost of cheap industrial food is externalized onto your health, your community, and your sovereignty."
        },
        
        keyPoints: [
          "Know your food chain: Can you trace your food back to its source? Do you know who grew it and how?",
          "Local over global: Food from 50 miles away is more resilient than food from 1,500 miles away",
          "Seasonal eating: Our ancestors ate what was available; fighting nature is expensive and fragile",
          "Preservation skills: Canning, fermenting, drying, and freezing extend the harvest year-round",
          "Start where you are: A windowsill herb garden is sovereignty; a backyard chicken is independence"
        ],
        
        tutorial: {
          title: "Building Your Local Food Network",
          steps: [
            {
              step: 1,
              title: "Map Local Food Sources",
              description: "Use LocalHarvest.org or search 'farmers market near me' to find local producers. Note CSAs, farm stands, and U-pick operations within 50 miles."
            },
            {
              step: 2,
              title: "Visit a Farmers Market",
              description: "Go this week. Talk to farmers. Ask where their farm is, how they grow, whether they sell directly. Build relationships."
            },
            {
              step: 3,
              title: "Start Growing Something",
              description: "Herbs are the easiest start: basil, mint, rosemary thrive in pots. Graduate to tomatoes, peppers, or greens. One plant is infinitely more than zero."
            },
            {
              step: 4,
              title: "Learn One Preservation Method",
              description: "Start with fermentation (sauerkraut, pickles) or freezing excess produce. These require minimal equipment and build valuable skills."
            },
            {
              step: 5,
              title: "Build a 2-Week Pantry",
              description: "Stock shelf-stable foods you actually eat: rice, beans, canned goods, pasta. Rotate regularly—this is about resilience, not hoarding."
            }
          ]
        },
        
        caseStudy: {
          title: "Victory Gardens: When America Fed Itself",
          content: "During World War II, 20 million American households planted 'Victory Gardens,' producing 40% of the nation's vegetables. Ordinary people—with no farming background—grew significant portions of their own food.\n\nThis wasn't survivalism; it was practical patriotism. Neighborhoods shared seeds, knowledge, and harvests. Communities became more resilient and connected.\n\nThe skills our great-grandparents took for granted—growing food, preserving harvests, cooking from scratch—aren't lost. They're simply waiting to be reclaimed. Every generation before us knew these things; we can learn them too."
        },
        
        expertQuote: {
          quote: "The greatest change we need to make is from consumption to production, even if on a small scale, in our own gardens. If only 10% of us do this, there is enough for everyone.",
          author: "Bill Mollison",
          context: "Father of Permaculture"
        },
        
        action: "Visit a local farmers market or research one in your area. Buy one item directly from a producer this week. Ask them about their farm and growing practices.",
        
        reflection: "What foods could you realistically grow or source locally? What's stopping you from starting? What did your grandparents grow or preserve that you've never learned?"
      },
      
      additionalResources: [
        {
          title: "The Market Gardener",
          author: "Jean-Martin Fortier",
          type: "Book",
          url: "https://www.amazon.com/Market-Gardener-Successful-Handbook-Small-scale/dp/0865717656",
          description: "Practical guide to productive small-scale organic growing."
        },
        {
          title: "Folks, This Ain't Normal",
          author: "Joel Salatin",
          type: "Book",
          url: "https://www.amazon.com/Folks-This-Aint-Normal-Healthier/dp/0892968206",
          description: "A farmer's perspective on food, freedom, and getting back to basics."
        },
        {
          title: "Nourishing Traditions",
          author: "Sally Fallon",
          type: "Book",
          url: "https://www.amazon.com/Nourishing-Traditions-Challenges-Politically-Dictocrats/dp/0967089735",
          description: "Traditional food preparation and the wisdom of ancestral diets."
        },
        {
          title: "LocalHarvest",
          author: "Various",
          type: "Website",
          url: "https://www.localharvest.org/",
          description: "Find farmers markets, CSAs, and local food sources near you."
        },
        {
          title: "The Fermentation Podcast",
          author: "Various",
          type: "Podcast",
          url: "https://fermentationpodcast.com/",
          description: "Learn the art and science of fermentation for food preservation."
        }
      ]
    },
    {
      day: 4,
      title: "Reclaiming Your Health",
      theme: "Health Autonomy",
      icon: "Heart",
      content: {
        intro: "The healthcare system is designed to treat symptoms, not create health. It profits from your sickness, not your vitality. Today we shift from reactive to proactive—from patient to sovereign—and explore what it means to take radical responsibility for your own well-being.",
        
        deepDive: {
          title: "From Sick Care to Self Care",
          content: "Americans spend more on healthcare than any nation in history—yet rank 35th in life expectancy. We've outsourced our health to a system with perverse incentives: it profits when we're sick and manages chronic conditions rather than curing them.\n\nConsider: 60% of Americans have at least one chronic disease. 40% have two or more. Most of these conditions—type 2 diabetes, heart disease, obesity—are lifestyle diseases that respond dramatically to changes in diet, movement, sleep, and stress.\n\nOur ancestors didn't have doctors or pharmacies, yet they didn't suffer from our modern epidemics. They moved constantly, ate whole foods, slept with the sun, and lived in tight-knit communities.\n\nHealth autonomy doesn't mean rejecting modern medicine when you need it. It means taking responsibility for the fundamentals that prevent most disease, building a relationship with your body, and becoming an informed advocate for your own care."
        },
        
        keyPoints: [
          "The four pillars: Sleep, nutrition, movement, and stress management address 80% of chronic disease",
          "Question nutritional orthodoxy: Much 'official' diet advice is influenced by industry, not science",
          "Movement over exercise: Constant low-level activity beats occasional intense workouts",
          "Sleep is non-negotiable: Poor sleep is linked to obesity, diabetes, heart disease, and cognitive decline",
          "Build your health team: Find practitioners who treat root causes, not just symptoms"
        ],
        
        tutorial: {
          title: "A Week of Health Sovereignty",
          steps: [
            {
              step: 1,
              title: "Audit Your Sleep",
              description: "Track your sleep for a week. Are you getting 7-9 hours? Is your room dark, cool, and screen-free? Is your wake time consistent?"
            },
            {
              step: 2,
              title: "Eliminate One Processed Food",
              description: "Identify one ultra-processed food you eat regularly (soda, chips, fast food). Replace it with a whole food alternative for one week."
            },
            {
              step: 3,
              title: "Add Daily Movement",
              description: "Walk for 30 minutes daily—not for exercise, but as transportation or exploration. Take calls while walking. Park far away."
            },
            {
              step: 4,
              title: "Practice Stress Intervention",
              description: "Add one stress-reduction practice: 5 minutes of deep breathing, cold shower, meditation, or time in nature. Do it daily for a week."
            },
            {
              step: 5,
              title: "Research One Natural Remedy",
              description: "Pick a common ailment you experience (headaches, poor sleep, digestive issues). Research natural approaches before reaching for pharmaceuticals."
            }
          ]
        },
        
        caseStudy: {
          title: "The Blue Zones: Where People Forget to Die",
          content: "Researchers identified five 'Blue Zones' where people regularly live to 100: Okinawa (Japan), Sardinia (Italy), Nicoya (Costa Rica), Ikaria (Greece), and Loma Linda (California).\n\nThese communities share common traits: plant-heavy diets, constant natural movement, strong social bonds, sense of purpose, and stress-reduction practices. Notably absent: gyms, supplements, and healthcare systems.\n\nThe lesson isn't to copy their specific diets or practices—it's that health emerges naturally from the right environment and habits. Our bodies are designed to be healthy; we've simply created environments that make disease inevitable."
        },
        
        expertQuote: {
          quote: "Let food be thy medicine and medicine be thy food.",
          author: "Hippocrates",
          context: "Father of Medicine, 400 BC"
        },
        
        action: "Identify one health habit you've been outsourcing (relying on pills, doctors, or products) that you could address through lifestyle changes. Commit to trying the natural approach for two weeks.",
        
        reflection: "When was the last time you felt truly healthy and energized? What was different about that time? What would it mean to be sovereign over your own health?"
      },
      
      additionalResources: [
        {
          title: "Nutrition and Physical Degeneration",
          author: "Weston A. Price",
          type: "Book",
          url: "https://www.amazon.com/Nutrition-Physical-Degeneration-Weston-Price/dp/0916764206",
          description: "Landmark study of traditional diets and their effects on health."
        },
        {
          title: "Why We Sleep",
          author: "Matthew Walker",
          type: "Book",
          url: "https://www.amazon.com/Why-We-Sleep-Unlocking-Dreams/dp/1501144316",
          description: "The science of sleep and why it's the foundation of health."
        },
        {
          title: "The Weston A. Price Foundation",
          author: "Various",
          type: "Website",
          url: "https://www.westonaprice.org/",
          description: "Resources on traditional diets, real food, and ancestral health."
        },
        {
          title: "Huberman Lab Podcast",
          author: "Andrew Huberman",
          type: "Podcast",
          url: "https://hubermanlab.com/",
          description: "Science-based tools for health, performance, and well-being."
        },
        {
          title: "Outlive: The Science and Art of Longevity",
          author: "Peter Attia",
          type: "Book",
          url: "https://www.amazon.com/Outlive-Science-Art-Longevity/dp/0593236599",
          description: "A physician's guide to extending healthspan, not just lifespan."
        }
      ]
    },
    {
      day: 5,
      title: "Energy & Infrastructure Independence",
      theme: "Resilience",
      icon: "Zap",
      content: {
        intro: "Modern life depends on invisible infrastructure: electricity, water, gas, internet. When these systems work, we never think about them. When they fail, everything stops. Today we examine our energy dependencies and explore alternatives that reduce our vulnerability to single points of failure.",
        
        deepDive: {
          title: "The Fragile Grid We Depend On",
          content: "The US electrical grid is aging, underfunded, and increasingly strained. The average American experiences power outages totaling over 7 hours per year—and that number is rising. Climate events, cyberattacks, and simple equipment failure all threaten a system we've built our entire lives around.\n\nBeyond electricity, consider your other infrastructure dependencies: municipal water (what if it was contaminated or shut off?), natural gas (do you have an alternative heat source?), internet (could you function without it for a week?).\n\nInfrastructure sovereignty isn't about building a bunker—it's about reducing single points of failure. A small solar system doesn't replace the grid, but it keeps your refrigerator running during an outage. A water filter doesn't replace municipal water, but it provides backup during a boil notice. A propane heater doesn't replace your furnace, but it keeps you warm when the power fails.\n\nThe goal is redundancy: multiple ways to meet essential needs."
        },
        
        keyPoints: [
          "Know your systems: Understand how electricity, water, and heat reach your home and where they can fail",
          "72-hour resilience: The minimum—be able to function for 3 days without external infrastructure",
          "Layered redundancy: Don't replace one dependency with another; build multiple backup options",
          "Skills over stuff: Knowing how to purify water matters more than owning the best filter",
          "Start with the basics: Water, warmth, and food preservation are the priorities"
        ],
        
        tutorial: {
          title: "Building 72-Hour Resilience",
          steps: [
            {
              step: 1,
              title: "Water Storage",
              description: "Store 1 gallon per person per day for 3 days minimum. A WaterBOB turns your bathtub into a 100-gallon reservoir during emergencies. Get a quality water filter (Sawyer, Berkey) for longer scenarios."
            },
            {
              step: 2,
              title: "Alternative Light",
              description: "Stock quality flashlights (headlamps are best), batteries, and candles. A small solar panel with USB can charge devices indefinitely."
            },
            {
              step: 3,
              title: "Backup Heat",
              description: "Know your options: propane heaters (Mr. Buddy), wood stove, or simply enough blankets and warm clothing to survive cold nights."
            },
            {
              step: 4,
              title: "Know Your Shutoffs",
              description: "Locate and learn to operate your main water shutoff, gas shutoff, and electrical panel. Practice now, not during an emergency."
            },
            {
              step: 5,
              title: "Food That Needs No Power",
              description: "Stock foods that require no refrigeration or cooking: canned goods, dried foods, nuts, crackers. Rotate regularly."
            }
          ]
        },
        
        caseStudy: {
          title: "Puerto Rico's 11-Month Blackout",
          content: "When Hurricane Maria hit Puerto Rico in 2017, it destroyed the island's electrical grid. The resulting blackout lasted 11 months in some areas—the longest in US history.\n\nResidents who adapted best had decentralized resources: solar panels, rainwater collection, gardens, and strong community networks. Those entirely dependent on the grid faced a humanitarian crisis.\n\nOne survivor's lesson: 'We thought the grid was temporary. Now I know: never rely on one system for anything essential. Solar panels, stored water, a garden—these aren't luxuries. They're insurance.'"
        },
        
        expertQuote: {
          quote: "The major problems in the world are the result of the difference between how nature works and the way people think.",
          author: "Gregory Bateson",
          context: "Systems Theorist"
        },
        
        action: "Prepare a basic 72-hour kit: water storage or filter, flashlights with batteries, non-perishable food. Locate and label your main water and gas shutoffs. Test your flashlights.",
        
        reflection: "How would a week without electricity change your daily life? What systems would you miss most? What single preparation would give you the most peace of mind?"
      },
      
      additionalResources: [
        {
          title: "When Technology Fails",
          author: "Matthew Stein",
          type: "Book",
          url: "https://www.amazon.com/When-Technology-Fails-Self-Reliance-Sustainability/dp/1933392452",
          description: "Comprehensive guide to resilience and self-reliance when systems fail."
        },
        {
          title: "The Prepared",
          author: "Various",
          type: "Website",
          url: "https://theprepared.com/",
          description: "Practical, non-alarmist guides to emergency preparedness."
        },
        {
          title: "Solar Power for Beginners",
          author: "Various",
          type: "Guide",
          url: "https://www.altestore.com/howto/",
          description: "Learn the basics of solar energy systems for homes."
        },
        {
          title: "Emergency Water Storage",
          author: "The Provident Prepper",
          type: "Guide",
          url: "https://theprovidentprepper.org/the-complete-water-storage-guide/",
          description: "Complete guide to storing and purifying water for emergencies."
        }
      ]
    },
    {
      day: 6,
      title: "Digital Privacy & Sovereignty",
      theme: "Digital Freedom",
      icon: "Shield",
      content: {
        intro: "Your data is currency—and you're giving it away for free. Every search, every message, every location is tracked, sold, and used to build a profile that predicts your behavior. Today we explore how to reclaim your digital life and reduce the surveillance that has become normalized.",
        
        deepDive: {
          title: "The Surveillance Economy",
          content: "When a product is free, you are the product. Google, Facebook, and countless other 'free' services profit by harvesting your attention and data, then selling access to your psychology to advertisers.\n\nAs Jameson Lopp notes: 'Privacy is not about having something to hide. Privacy is about having the power to selectively reveal yourself to the world. Without privacy, you have no autonomy.'\n\nThis isn't paranoia—it's documented fact. Your phone tracks your location constantly. Your email is scanned for advertising keywords. Your browsing history builds a profile more intimate than what you'd share with your closest friend.\n\nDigital sovereignty isn't about going completely off-grid (though some choose that). It's about making conscious choices: which data do you want to share, with whom, and for what benefit? It's about reducing your attack surface and building habits that protect your autonomy.\n\nThe good news: small changes compound. Switching your browser, using a VPN, choosing encrypted messaging—each step makes you harder to track and profile."
        },
        
        keyPoints: [
          "Audit your digital footprint: What data are you sharing? Who has access? What could they do with it?",
          "Browser matters: Switch from Chrome to Brave or Firefox—Google Chrome is a surveillance tool",
          "Encrypt communications: Signal for messaging, ProtonMail for email—make eavesdropping difficult",
          "Minimize data collection: Disable location services, use burner emails, avoid loyalty programs",
          "Self-host when possible: Control your own data with self-hosted alternatives to cloud services"
        ],
        
        tutorial: {
          title: "A Week of Digital Sovereignty",
          steps: [
            {
              step: 1,
              title: "Switch Your Browser",
              description: "Download Brave or Firefox. Import your bookmarks from Chrome. Use DuckDuckGo or Brave Search instead of Google."
            },
            {
              step: 2,
              title: "Secure Your Passwords",
              description: "Install Bitwarden (free, open-source password manager). Generate unique passwords for every account. Enable 2FA wherever possible."
            },
            {
              step: 3,
              title: "Encrypt Your Messages",
              description: "Download Signal. Convince your close contacts to switch. It's as easy as regular texting but fully encrypted."
            },
            {
              step: 4,
              title: "Audit App Permissions",
              description: "Review which apps have access to your location, microphone, and camera. Disable permissions for apps that don't need them."
            },
            {
              step: 5,
              title: "Consider a VPN",
              description: "Research VPN options (Mullvad, ProtonVPN, IVPN). A VPN hides your browsing from your ISP and adds a layer of privacy."
            }
          ]
        },
        
        caseStudy: {
          title: "The Cambridge Analytica Scandal",
          content: "In 2018, it was revealed that Cambridge Analytica had harvested data from 87 million Facebook users without consent, using it to build psychological profiles for political manipulation.\n\nThe data wasn't stolen through hacking—it was freely given through a personality quiz app. Users had voluntarily shared access to their profiles and their friends' profiles, never imagining how that data would be used.\n\nThe lesson: 'free' services have costs you don't see. Every quiz, every app permission, every 'Sign in with Facebook' is a trade you're making. Understanding the currency of data is the first step to spending it wisely."
        },
        
        expertQuote: {
          quote: "Privacy is necessary for an open society in the electronic age. Privacy is not secrecy. A private matter is something one doesn't want the whole world to know, but a secret matter is something one doesn't want anybody to know. Privacy is the power to selectively reveal oneself to the world.",
          author: "Eric Hughes",
          context: "A Cypherpunk's Manifesto, 1993"
        },
        
        action: "Switch to a privacy-focused browser (Brave or Firefox) and install a password manager (Bitwarden) if you don't have one. Audit your phone's app permissions this week.",
        
        reflection: "What would change if you treated your personal data as valuable property? What 'free' services might not be worth the cost? What digital habits would a sovereign person have?"
      },
      
      additionalResources: [
        {
          title: "Personal Security Resources",
          author: "Jameson Lopp",
          type: "Website",
          url: "https://www.lopp.net/bitcoin-information/security.html",
          description: "Comprehensive guide to personal security and privacy from a Bitcoin security expert."
        },
        {
          title: "EFF Surveillance Self-Defense",
          author: "Electronic Frontier Foundation",
          type: "Guide",
          url: "https://ssd.eff.org/",
          description: "Free guide to protecting yourself from online surveillance."
        },
        {
          title: "Privacy Tools",
          author: "PrivacyTools.io",
          type: "Website",
          url: "https://www.privacytools.io/",
          description: "Curated list of privacy-focused software alternatives."
        },
        {
          title: "The Age of Surveillance Capitalism",
          author: "Shoshana Zuboff",
          type: "Book",
          url: "https://www.amazon.com/Age-Surveillance-Capitalism-Future-Frontier/dp/1610395697",
          description: "Deep analysis of how tech companies monetize our behavior and data."
        },
        {
          title: "Digital Security for Beginners",
          author: "Access Now",
          type: "Guide",
          url: "https://www.accessnow.org/help/",
          description: "Free security helpline and resources for activists and at-risk users."
        }
      ]
    },
    {
      day: 7,
      title: "Building Your Sovereign Future",
      theme: "Integration",
      icon: "Target",
      content: {
        intro: "You've spent a week examining, questioning, and taking first steps. Today we create a sustainable path forward—not perfection, but progress. Sovereignty isn't a destination; it's a direction. The goal is to be more sovereign tomorrow than you are today.",
        
        deepDive: {
          title: "From Information to Transformation",
          content: "Knowledge without action is merely entertainment. This week, you've consumed a lot of information across many domains: money, food, health, energy, digital life. The challenge now is integration—turning insight into habit.\n\nThe trap to avoid: trying to change everything at once. This leads to overwhelm, burnout, and retreat to old patterns. Instead, focus on the 80/20 principle: which 20% of changes will produce 80% of your sovereignty gains?\n\nFor most people, financial sovereignty (Bitcoin self-custody) and health autonomy (sleep, nutrition, movement) produce the largest returns. But your priorities may differ based on your dependency audit from Day 1.\n\nSovereignty is also not isolation. The most resilient communities throughout history have been networks of interdependent individuals, each with skills and resources to share. Find your tribe—locally or online—of people walking this path.\n\nFinally, remember: you're not behind. Every person further along this path once started exactly where you are now. The best time to start was twenty years ago. The second best time is today."
        },
        
        keyPoints: [
          "Review your week: What insights resonated most deeply? What actions did you actually take?",
          "Prioritize ruthlessly: Pick 2-3 areas to focus on over the next 90 days—not everything at once",
          "Build systems, not goals: Habits beat intentions; make sovereignty automatic",
          "Find your community: Sovereignty isn't isolation—it's intentional connection with aligned people",
          "Measure progress, not perfection: Are you more sovereign this month than last month?"
        ],
        
        tutorial: {
          title: "Creating Your 90-Day Sovereignty Plan",
          steps: [
            {
              step: 1,
              title: "Review Your Dependency Audit",
              description: "Look at Day 1's audit. Which top 3 vulnerabilities are you most motivated to address? Which feel most urgent?"
            },
            {
              step: 2,
              title: "Set 3 Specific Goals",
              description: "Choose one goal for each vulnerability. Make it specific and measurable: 'Move $500 to Bitcoin self-custody' not 'Learn about Bitcoin.'"
            },
            {
              step: 3,
              title: "Define Weekly Actions",
              description: "Break each goal into weekly actions. What will you do THIS WEEK toward each goal? Put it in your calendar."
            },
            {
              step: 4,
              title: "Find Accountability",
              description: "Share your plan with someone who will check in. This could be a spouse, friend, or online community. Accountability multiplies follow-through."
            },
            {
              step: 5,
              title: "Schedule a 90-Day Review",
              description: "Put a date in your calendar 90 days from now. On that day, you'll review progress and set new goals. This creates a sovereignty practice, not a one-time event."
            }
          ]
        },
        
        caseStudy: {
          title: "The Sovereignty Spectrum",
          content: "Consider three people at different points on the sovereignty journey:\n\nAlex just started. He switched to Brave browser, bought $100 of Bitcoin on an exchange, and started walking 30 minutes daily. Small changes—but he's started.\n\nMaria is mid-journey. She has 6 months of expenses in self-custody Bitcoin, grows 20% of her vegetables, uses Signal for messaging, and has a 2-week emergency food supply. She's still dependent on many systems, but her vulnerabilities are reduced.\n\nDavid is advanced. He lives on rural property with solar power and well water, raises most of his food, operates a Bitcoin node, homeschools his kids, and has deep community ties. He's not 'off-grid'—he's selectively engaged with systems that serve him.\n\nAll three are sovereign. Sovereignty isn't binary—it's a spectrum. The question isn't 'Are you sovereign?' but 'Are you more sovereign than yesterday?'"
        },
        
        expertQuote: {
          quote: "We are what we repeatedly do. Excellence, then, is not an act, but a habit.",
          author: "Aristotle",
          context: "Via Will Durant"
        },
        
        action: "Write a 90-day sovereignty plan using the tutorial above. Choose 3 specific, measurable goals. Share your plan with someone who will hold you accountable. Schedule your 90-day review now.",
        
        reflection: "How has your understanding of sovereignty changed this week? What does 'unshackled living' mean to you now? Who in your life might benefit from this journey?"
      },
      
      additionalResources: [
        {
          title: "Atomic Habits",
          author: "James Clear",
          type: "Book",
          url: "https://www.amazon.com/Atomic-Habits-Proven-Build-Break/dp/0735211299",
          description: "The science of building habits that stick—essential for sustained change."
        },
        {
          title: "The Sovereign Individual",
          author: "James Dale Davidson & William Rees-Mogg",
          type: "Book",
          url: "https://www.amazon.com/Sovereign-Individual-Mastering-Transition-Information/dp/0684832720",
          description: "Prescient book on how technology is enabling individual sovereignty."
        },
        {
          title: "Unshackled Living Podcast",
          author: "Jarrett",
          type: "Podcast",
          url: "https://www.youtube.com/@UnshackledLiving",
          description: "Continue your sovereignty journey with weekly deep dives into all aspects of unshackled living."
        },
        {
          title: "Sovereignty Communities",
          author: "Various",
          type: "Community",
          url: "https://bitcointalk.org/",
          description: "Connect with others on the sovereignty path. Local meetups, online forums, and Bitcoin communities."
        }
      ]
    }
  ]
};

export const getJournalPrompt = (day) => {
  return courseData.days[day - 1]?.content.reflection || "Reflect on your progress today.";
};
